import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import WizardShell, {
    WizardStep,
} from '../../../components/wizard/WizardShell';
import {
    BundleIssue,
    DeviceBundlePreflight,
    getDeviceBundlePreflight,
    submitDeviceBundle,
} from '../../../api/bundle';
import { ApiError } from '../../../api/request';
import { useCan, useMe } from '../../../store';
import { confirmDialog } from '../../../utils/dialog';
import StepDeviceBasic from './StepDeviceBasic';
import StepModes from './StepModes';
import StepDeviceFields from './StepDeviceFields';
import StepCalibration from './StepCalibration';
import StepDeviceParams from './StepDeviceParams';
import StepDeviceReview from './StepDeviceReview';
import {
    clearDraft,
    DEVICE_STEPS,
    DeviceStepKey,
    emptyDevicePayload,
    hasContent,
    newDictCount,
    stepOf,
    toRequest,
    useDeviceBundleDraft,
} from './useDeviceBundleDraft';

// 设备创建向导 (2026-09-17, 设计 doc/relate-import/07 P2).
//
// 「新建设备」的唯一入口 —— 原来的新增设备弹窗随本页下线。
// 6 步填出来的是一份设备套件载荷, 提交后一个审批工单、一个事务落全部内容
// (设备 + 模式引用/开关 + 字段组值 + 标定曲线 + 参数行 + 模板绑定), 任一步失败整体回滚。
// 2026-09-22 起向导与套件文件导入能力对齐 (单设备): 后端 DeviceBundle 的每一段都能在向导里填。
//
// 相对旧弹窗的实质改进: 旧弹窗是两段式写入 (先建设备再写 ext 值), 新建走审批时设备
// 还没落库, 能力字段无处可写只能丢弃, 审批通过后还得去工况页重填一遍。套件没有这个洞。

function DeviceCreateWizard() {
    const history = useHistory();
    const draft = useDeviceBundleDraft();
    const { payload, patchDevice, setPayload, step, goto, report, checking } =
        draft;
    const canPublish = useCan('device', 'publish');
    // 草稿按登录用户分开存 (共享工位上换人不该看见别人的半张表单)
    const { username } = useMe();

    const [preflight, setPreflight] = useState<DeviceBundlePreflight | null>(
        null,
    );
    const [ack, setAck] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitErr, setSubmitErr] = useState('');
    const [done, setDone] = useState<{
        taskCode?: string;
        created?: number;
    } | null>(null);

    const { archCode } = payload.device;

    // 框架定了就拉 preflight: 字段组定义与模式字典从这里来.
    // reloadNonce: 向导里就地新建了字段组之后要重取一次, 否则新组要等重进向导才看得见.
    const [reloadNonce, setReloadNonce] = useState(0);
    const reloadPreflight = useCallback(() => setReloadNonce((n) => n + 1), []);
    useEffect(() => {
        if (!archCode) {
            setPreflight(null);
            return;
        }
        let alive = true;
        getDeviceBundlePreflight(archCode)
            .then((d) => alive && setPreflight(d))
            .catch(() => alive && setPreflight(null));
        return () => {
            alive = false;
        };
    }, [archCode, reloadNonce]);

    const extCount = Object.keys(payload.extValues || {}).length;
    // 字段组总数含本套件待新建的草稿组 (newDicts.deviceSchemas)
    const groupTotal =
        (preflight?.deviceSchemas || []).length +
        (payload.newDicts?.deviceSchemas?.length || 0);

    const steps: WizardStep[] = useMemo(
        () =>
            DEVICE_STEPS.map((s) => ({
                key: s.key,
                label: s.label,
                optional: s.optional,
                hasError: draft.issuesByStep[s.key].some(
                    (i) => i.severity === 'error',
                ),
                badge:
                    s.key === 'modes'
                        ? payload.modeRefs?.length
                            ? `${payload.modeRefs.length} 个`
                            : ''
                        : s.key === 'fields'
                          ? groupTotal
                              ? `${extCount}/${groupTotal} 组`
                              : ''
                          : s.key === 'calib'
                            ? payload.calibration?.length
                                ? `${payload.calibration.length} 点`
                                : ''
                            : s.key === 'params'
                              ? payload.params?.length
                                  ? `${payload.params.length} 行`
                                  : ''
                              : '',
            })),
        [draft.issuesByStep, payload, extCount, groupTotal],
    );

    const summary = useMemo(
        () => [
            {
                label: '设备',
                value: payload.device.deviceKey || (
                    <span className="text-slate-300">未填</span>
                ),
            },
            {
                label: '名称',
                value: payload.device.name || (
                    <span className="text-slate-300">—</span>
                ),
            },
            {
                label: '框架',
                value: payload.device.archCode || (
                    <span className="text-slate-300">未选</span>
                ),
            },
            {
                label: '机型',
                value: payload.device.family || (
                    <span className="text-slate-300">未选</span>
                ),
            },
            { label: '加工模式', value: payload.modeRefs?.length || '—' },
            { label: '模式开关', value: payload.modeSwitches?.length || '—' },
            {
                label: '字段组',
                value: groupTotal ? `${extCount}/${groupTotal} 组` : '—',
            },
            {
                label: '标定点',
                value: payload.calibration?.length
                    ? `${payload.calibration.length} 点`
                    : '—',
            },
            { label: '参数行', value: payload.params?.length || '—' },
            {
                label: '随套件新建',
                value: newDictCount(payload.newDicts) || '—',
            },
        ],
        [payload, extCount, groupTotal],
    );

    const onGotoIssue = useCallback(
        (issue: BundleIssue) => goto(stepOf(issue.path)),
        [goto],
    );

    const checkPanel = (
        <div className="border-t border-slate-100 pt-2">
            <div className="text-xs font-semibold text-slate-600 mb-1.5">
                预检
            </div>
            {!report ? (
                <div className="text-[11px] text-slate-400">尚未预检</div>
            ) : (
                <div className="space-y-1">
                    <CheckGroup
                        tone="text-rose-600"
                        icon="✖"
                        label="错误"
                        issues={report.errors}
                        emptyLabel="✔ 无错误"
                        emptyTone="text-emerald-600"
                        onGoto={onGotoIssue}
                    />
                    <CheckGroup
                        tone="text-amber-600"
                        icon="⚠"
                        label="告警"
                        issues={report.warnings}
                        onGoto={onGotoIssue}
                    />
                    <CheckGroup
                        tone="text-slate-400"
                        icon="ⓘ"
                        label="提示"
                        issues={report.infos}
                        onGoto={onGotoIssue}
                    />
                </div>
            )}
        </div>
    );

    const idx = DEVICE_STEPS.findIndex((s) => s.key === step);
    const isLast = step === 'review';

    const next = async () => {
        if (step === 'basic') {
            const d = payload.device;
            if (!d.deviceKey || !d.archCode || !d.family) {
                setSubmitErr('device_key / 工艺框架 / 机型 都必填');
                return;
            }
            setSubmitErr('');
        }
        await draft.runCheck();
        goto(DEVICE_STEPS[Math.min(idx + 1, DEVICE_STEPS.length - 1)].key);
    };

    const submit = async () => {
        setSubmitting(true);
        setSubmitErr('');
        try {
            const { data, message } = await submitDeviceBundle(
                toRequest(payload),
                {
                    ackWarnings: ack,
                },
            );
            // 服务端会重跑一遍预检, 报告以它为准 (前置数据可能已变; 被拒时界面要能看到真实告警)
            if (data.report) draft.adoptReport(data.report);
            if (data.accepted === false) {
                setSubmitErr(message || '提交被拒绝, 请按报告修改后重试');
                return;
            }
            clearDraft(username);
            setDone({ taskCode: data.taskCode, created: data.created });
        } catch (e) {
            setSubmitErr(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
        } finally {
            setSubmitting(false);
        }
    };

    const leave = async () => {
        if (!done && hasContent(payload)) {
            const ok = await confirmDialog(
                '离开向导? 已填内容会保留为草稿, 下次进入可恢复。',
            );
            if (!ok) return;
        }
        history.push('/devices/manage');
    };

    // stale: 上次预检之后载荷改过 (如从「跳过」/步骤栏进来), 旧报告不能放行, 等自动重跑出结果
    // 门禁用服务端原始判定 (draft.rawReport): 展示用的 report 会把"改过的那几条"先撤掉,
    // 拿它当门禁等于自己给自己放行。
    const raw = draft.rawReport;
    const canSubmit =
        !!raw &&
        !draft.stale &&
        raw.ok &&
        (raw.warnings.length === 0 || ack) &&
        !submitting &&
        !checking;

    const nav = done ? (
        <>
            <button
                type="button"
                className="btn-ghost"
                onClick={() => window.location.reload()}
            >
                继续新建
            </button>
            <button
                type="button"
                className="btn-primary"
                onClick={() => history.push('/devices/manage')}
            >
                回到设备管理
            </button>
        </>
    ) : (
        <>
            <button
                type="button"
                className="btn-ghost"
                disabled={idx === 0}
                onClick={() => goto(DEVICE_STEPS[Math.max(idx - 1, 0)].key)}
            >
                上一步
            </button>
            {!isLast && DEVICE_STEPS[idx].optional && (
                <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => goto(DEVICE_STEPS[idx + 1].key)}
                >
                    跳过
                </button>
            )}
            {isLast ? (
                <button
                    type="button"
                    className="btn-primary"
                    disabled={!canSubmit}
                    onClick={submit}
                >
                    {submitting ? '提交中…' : '提交'}
                </button>
            ) : (
                <button
                    type="button"
                    className="btn-primary"
                    disabled={checking}
                    onClick={next}
                >
                    {checking ? '预检中…' : '下一步 →'}
                </button>
            )}
        </>
    );

    return (
        <WizardShell
            title="新建设备"
            backLabel="返回设备管理"
            onBack={leave}
            steps={steps}
            current={step}
            onStep={(k) => goto(k as DeviceStepKey)}
            summary={summary}
            checkPanel={checkPanel}
            nav={nav}
        >
            {(submitErr || draft.error) && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    {submitErr || draft.error}
                </div>
            )}

            {draft.restoredAt > 0 && (
                <div className="warnbox text-xs flex items-center gap-2">
                    <span>
                        已恢复上次未完成的草稿 (
                        {new Date(draft.restoredAt).toLocaleString('zh-CN')})
                    </span>
                    <button
                        type="button"
                        className="ml-auto text-brand-600 hover:underline"
                        onClick={() => {
                            clearDraft(username);
                            setPayload(emptyDevicePayload());
                            draft.dismissRestored();
                        }}
                    >
                        丢弃草稿, 从头开始
                    </button>
                    <button
                        type="button"
                        className="text-slate-500 hover:underline"
                        onClick={draft.dismissRestored}
                    >
                        知道了
                    </button>
                </div>
            )}

            {step === 'basic' && (
                <StepDeviceBasic
                    payload={payload}
                    patch={patchDevice}
                    setPayload={setPayload}
                    preflight={preflight}
                    issueByPath={draft.issueByPath}
                />
            )}
            {step === 'modes' && (
                <StepModes
                    payload={payload}
                    setPayload={setPayload}
                    issueByPath={draft.issueByPath}
                    preflight={preflight}
                />
            )}
            {step === 'fields' && (
                <StepDeviceFields
                    payload={payload}
                    setPayload={setPayload}
                    preflight={preflight}
                    issueByPath={draft.issueByPath}
                />
            )}
            {step === 'calib' && (
                <StepCalibration
                    payload={payload}
                    setPayload={setPayload}
                    issueByPath={draft.issueByPath}
                />
            )}
            {step === 'params' && (
                <StepDeviceParams
                    payload={payload}
                    setPayload={setPayload}
                    preflight={preflight}
                    issueByPath={draft.issueByPath}
                    onSchemaChanged={reloadPreflight}
                />
            )}
            {step === 'review' && (
                <StepDeviceReview
                    payload={payload}
                    patch={patchDevice}
                    report={report}
                    checking={checking}
                    ack={ack}
                    setAck={setAck}
                    canPublish={canPublish}
                    onGoto={onGotoIssue}
                    done={done}
                />
            )}
        </WizardShell>
    );
}

/** CheckGroup 右栏预检的一档: 点计数展开具体条目, 每条可点跳到出问题的那一步. */
function CheckGroup({
    tone,
    icon,
    label,
    issues,
    emptyLabel,
    emptyTone,
    onGoto,
}: {
    tone: string;
    icon: string;
    label: string;
    issues: BundleIssue[];
    emptyLabel?: string;
    emptyTone?: string;
    onGoto: (i: BundleIssue) => void;
}) {
    const [open, setOpen] = useState(false);
    if (issues.length === 0) {
        if (!emptyLabel) return null;
        return (
            <div className={`text-[11px] ${emptyTone || tone}`}>
                {emptyLabel}
            </div>
        );
    }
    return (
        <div>
            <button
                type="button"
                className={`text-[11px] ${tone} hover:underline flex items-center gap-1`}
                onClick={() => setOpen(!open)}
            >
                <span>
                    {icon} {issues.length} 条{label}
                </span>
                <span className="opacity-60">{open ? '▾' : '▸'}</span>
            </button>
            {open && (
                <div className="mt-1 space-y-1">
                    {issues.map((i, k) => (
                        <button
                            key={`${i.code}-${i.path}-${k}`}
                            type="button"
                            className="block w-full text-left text-[11px] leading-snug text-slate-600 bg-slate-50 hover:bg-slate-100 rounded px-1.5 py-1"
                            title={`${i.msg}\n${i.path}`}
                            onClick={() => onGoto(i)}
                        >
                            {i.msg}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default DeviceCreateWizard;
